import { isEqual } from 'lodash';

import { hasEmail, isValidEmail } from '../../../utils/inputValidation';
import { type UserFromCSV } from '../components/AdminUsers/types';
import { blankUser } from '../constants/admin-users';
import { type User, type UserRole } from '../constants/user-roles';

export type EmailErrorKeys = 'required-email' | 'invalid-email' | 'duplicate-email' | '';
export type NameErrorKeys = 'required-name' | 'invalid-name' | '';
export type RoleErrorKeys = 'required-role' | 'invalid-role' | '';

export const emailValidation = (email?: string): EmailErrorKeys => {
    if (!hasEmail(email)) return 'required-email';
    if (email && !isValidEmail(email)) return 'invalid-email';

    return '';
};

export const findDuplicateEmailIndexes = <T extends { email?: string }>(users: T[]) => {
    const seenEmails: string[] = [];
    const indexesOfDuplicates: number[] = [];

    users.forEach((user, index) => {
        if (user.email) {
            if (seenEmails.includes(user.email)) indexesOfDuplicates.push(index);
            else seenEmails.push(user.email);
        }
    });

    return indexesOfDuplicates;
};

const hasName = (firstName: string): boolean => firstName.trim().length > 0;

const isValidName = (name: unknown): boolean => typeof name === 'string';

export const nameValidation = (name: string): NameErrorKeys => {
    if (!isValidName(name)) return 'invalid-name';
    if (!hasName(name)) return 'required-name';

    return '';
};

export const roleValidation = (role: UserRole[]): RoleErrorKeys => (role.length === 0 ? 'required-role' : '');

export const validateUsers = (users: User[]) => {
    const newErrorIndexes: number[] = [];
    const indexesOfDuplicateEmails = findDuplicateEmailIndexes(users);

    users.forEach((user, index) => {
        if (isEqual(user, blankUser)) return; // ignore blank rows
        if (
            nameValidation(user.name) !== '' ||
            emailValidation(user.email) !== '' ||
            indexesOfDuplicateEmails.includes(index)
        ) {
            newErrorIndexes.push(index);
        }
    });

    return newErrorIndexes;
};

export const validateParsedUsers = ({ users, maxCsvRows }: { users: UserFromCSV[]; maxCsvRows: number }) => {
    const limitExceeded = users.length > maxCsvRows;

    const newErrorIndexes: number[] = [];
    const indexesOfDuplicateEmails = findDuplicateEmailIndexes(users);

    users.forEach((user, index) => {
        if (isEqual(user, blankUser)) return; // ignore blank rows
        if (nameValidation(user.name) !== '' || emailValidation(user.email) !== '') {
            newErrorIndexes.push(index);
        }
    });

    return {
        limitExceeded,
        newErrorIndexes,
        indexesOfDuplicateEmails
    };
};
